/**
 * Corrections: people's edits kept as a layer over the analysis (backend: services/video/corrections.py, the same
 * rule; __fixtures__/corrections_case_*.json is the backend's tests/video/fixtures copy and keeps the two in step).
 *
 * Only `ready` edits count, plus an `add` still `following` or `failed` (shown at once as one box on its frame);
 * the assistant's `proposed` edits and `unmatched` ones after a re-run do not. `remove` and `negative` drop a track,
 * `merge` joins tracks into the first (the earlier track's point wins a shared frame, earliest start, highest
 * confidence), `add` appends a track with id ADDED_ID_BASE + its `seq` (else its 1-based place in the edit list),
 * counted from its first frame and at full confidence so the sensitivity slider never hides it. Added tracks are made
 * first, so a merge can join an added object with one the analysis found.
 */

export const ADDED_ID_BASE = 1000000;
const ACTIVE = 'ready';
const SHOWN_ADD = ['ready', 'following', 'failed'];

const boxTrack = (frame, [x1, y1, x2, y2]) => ({
	t: [frame],
	x: [Math.round((x1 + x2) / 2)],
	y: [Math.round((y1 + y2) / 2)],
	w: [Math.round(x2 - x1)],
	h: [Math.round(y2 - y1)],
});

const merge = tracks => {
	const points = new Map();
	tracks.forEach(tr =>
		tr.t.forEach((t, j) => {
			if (!points.has(t)) points.set(t, [tr.x[j], tr.y[j], tr.w[j], tr.h[j]]);
		})
	);
	const frames = [...points.keys()].sort((a, b) => a - b);
	return {
		...tracks[0],
		t: frames,
		x: frames.map(f => points.get(f)[0]),
		y: frames.map(f => points.get(f)[1]),
		w: frames.map(f => points.get(f)[2]),
		h: frames.map(f => points.get(f)[3]),
		t0: frames[0],
		confirmed_at: Math.min(...tracks.map(tr => tr.confirmed_at ?? tr.t0 ?? tr.t[0])),
		conf: Math.max(...tracks.map(tr => tr.conf || 0)),
	};
};

/** The id of an added object's track (backend: corrections.added_id). */
export const addedId = (edit, place) => ADDED_ID_BASE + (edit.seq || place);

/** How a track is named to people: "#12", or "added #3" for the third object someone added. */
export const trackName = id => (id >= ADDED_ID_BASE ? `added #${id - ADDED_ID_BASE}` : `#${id}`);

// the tracks people added: followed ones as followed, the others as one box on their frame
const addedTracks = edits => {
	const out = [];
	edits.forEach((e, k) => {
		if (e.type !== 'add' || !SHOWN_ADD.includes(e.status)) return;
		let shape = e.status === ACTIVE && e.track ? e.track : null;
		if (!shape) {
			if (e.frame == null || !e.box) return;
			shape = boxTrack(e.frame, e.box);
		}
		const t0 = shape.t[0];
		out.push({
			id: addedId(e, k + 1),
			t0,
			confirmed_at: t0,
			rel_size: 1,
			conf: 1,
			added: true,
			edit: e.id,
			...(e.label ? { label: e.label } : {}),
			t: [...shape.t],
			x: [...shape.x],
			y: [...shape.y],
			w: [...shape.w],
			h: [...shape.h],
		});
	});
	return out;
};

/** tracks.json with the layer's edits applied (a new document; neither input is changed). */
export const applyCorrections = (doc, layer) => {
	const edits = layer?.edits || [];
	if (!doc || !edits.length) return doc;
	const tracks = [...(doc.tracks || []), ...addedTracks(edits)];
	const byId = new Map(tracks.map(tr => [tr.id, tr]));
	const gone = new Set();
	edits.forEach(e => {
		if (e.status === ACTIVE && (e.type === 'remove' || e.type === 'negative'))
			(e.tracks || []).forEach(t => gone.add(t));
	});
	const mergedInto = new Map();
	edits.forEach(e => {
		if (e.status !== ACTIVE || e.type !== 'merge') return;
		const ids = (e.tracks || []).filter(t => byId.has(t) && !gone.has(t));
		if (ids.length < 2) return;
		mergedInto.set(ids[0], merge(ids.map(i => mergedInto.get(i) || byId.get(i))));
		ids.slice(1).forEach(i => gone.add(i));
	});
	const out = tracks.filter(tr => !gone.has(tr.id)).map(tr => mergedInto.get(tr.id) || tr);
	return { ...doc, tracks: out };
};

/** How many tracks the layer added, removed and merged away (what the count panel says next to the count). */
export const correctionsSummary = (doc, layer) => {
	const edits = layer?.edits || [];
	const ids = new Set([...(doc?.tracks || []), ...addedTracks(edits)].map(tr => tr.id));
	const removed = new Set();
	edits.forEach(e => {
		if (e.status === ACTIVE && (e.type === 'remove' || e.type === 'negative'))
			(e.tracks || []).forEach(t => ids.has(t) && removed.add(t));
	});
	const merged = edits.filter(
		e =>
			e.status === ACTIVE &&
			e.type === 'merge' &&
			new Set((e.tracks || []).filter(t => ids.has(t) && !removed.has(t))).size >= 2
	).length;
	const added = edits.filter(e => e.type === 'add' && SHOWN_ADD.includes(e.status)).length;
	return { added, removed: removed.size, merged };
};

export { withArticle } from './subjectWords';
